/**
 * @file userStatusStyles.ts
 * @brief Class-name helpers keyed off a user's status (no JSX).
 *
 * @details
 * Split out of the former `UserStatusBadge.tsx` (02-08-2026), which paired a
 * badge component with this helper and so lost React Fast Refresh — a module
 * exporting both a component and a value cannot be hot-swapped by
 * `@vitejs/plugin-react`, and full-reloads the page on every edit. The badge
 * itself turned out to have no caller and was deleted in the same pass: since
 * the Status column was dropped (29-07-2026), user status is shown **only** as
 * the timeline spine colour, which is what this returns.
 *
 * @references
 * - features/users/UsersPage.tsx (sole consumer)
 *
 * @author DES
 * @date 02-08-2026
 */

import type { UserStatus } from "@/shared/types/api";

/** Timeline spine node colors — mirrors badge success / warning / destructive. */
export function userStatusTimelineMarkerClass(
  status: UserStatus | string | null | undefined,
): string {
  switch (status) {
    case "active":
      return "border-success bg-success-soft group-hover/trow:border-success group-hover/trow:bg-success/25";
    case "suspended":
      return "border-warning bg-warning-soft group-hover/trow:border-warning group-hover/trow:bg-warning/25";
    case "deleted":
      return "border-destructive bg-destructive/15 group-hover/trow:border-destructive group-hover/trow:bg-destructive/25";
    default:
      return "border-muted-foreground/40 bg-muted group-hover/trow:border-muted-foreground/60 group-hover/trow:bg-muted";
  }
}
